import { useState, useMemo } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Eye, Code, FileText } from 'lucide-react';

export default function PromptPreview({ content, variables, highlightedContent }) {
  const [previewVars, setPreviewVars] = useState({});
  const [viewMode, setViewMode] = useState('highlighted');

  // Render prompt with variable values
  const renderedPrompt = useMemo(() => {
    let rendered = content;
    Object.entries(previewVars).forEach(([key, value]) => {
      if (value) {
        rendered = rendered.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
      }
    });
    return rendered;
  }, [content, previewVars]);

  // Count filled variables
  const filledCount = Object.values(previewVars).filter(v => v).length;

  if (!content) {
    return (
      <div className="h-full flex items-center justify-center text-[#64748B] text-sm">
        No content to preview
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Variable Inputs */}
      {variables.length > 0 && (
        <div className="p-4 border-b border-[#1E293B] bg-[#030712] flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs text-[#94A3B8]">Preview Variables</Label>
            <Badge variant="outline" className="text-[10px]">
              {filledCount}/{variables.length} filled
            </Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-32 overflow-auto">
            {variables.map(v => (
              <div key={v} className="space-y-1">
                <Label className="text-[10px] text-[#64748B] font-mono">{`{{${v}}}`}</Label>
                <Input
                  value={previewVars[v] || ''}
                  onChange={(e) => setPreviewVars(prev => ({ ...prev, [v]: e.target.value }))}
                  className="h-8 text-xs bg-[#0F172A] border-[#1E293B]"
                  placeholder={`${v}...`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="flex-1 flex flex-col min-h-0">
        <div className="px-4 pt-2 border-b border-[#1E293B] flex-shrink-0">
          <TabsList className="bg-transparent h-8">
            <TabsTrigger 
              value="highlighted" 
              className="text-xs data-[state=active]:bg-[#1E293B]"
            >
              <Code className="w-3 h-3 mr-1.5" />
              Highlighted
            </TabsTrigger>
            <TabsTrigger 
              value="rendered"
              className="text-xs data-[state=active]:bg-[#1E293B]"
            >
              <Eye className="w-3 h-3 mr-1.5" />
              Rendered
            </TabsTrigger>
            <TabsTrigger 
              value="markdown"
              className="text-xs data-[state=active]:bg-[#1E293B]"
            >
              <FileText className="w-3 h-3 mr-1.5" />
              Markdown
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="highlighted" className="flex-1 m-0 overflow-auto min-h-0">
          <div className="p-4">
            <pre 
              className="font-mono text-sm text-[#F1F5F9] whitespace-pre-wrap leading-relaxed"
              dangerouslySetInnerHTML={{ __html: highlightedContent }}
            />
          </div>
        </TabsContent>

        <TabsContent value="rendered" className="flex-1 m-0 overflow-auto min-h-0">
          <div className="p-4">
            <pre className="font-mono text-sm text-[#F1F5F9] whitespace-pre-wrap leading-relaxed">
              {renderedPrompt}
            </pre>
            {variables.some(v => renderedPrompt.includes(`{{${v}}}`)) && (
              <div className="mt-4 p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm">
                <p className="text-xs text-[#F59E0B]">
                  Some variables are not filled. Fill them above to see the complete rendered prompt.
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="markdown" className="flex-1 m-0 overflow-auto min-h-0">
          <div className="p-4 prose prose-invert prose-sm max-w-none">
            <div className="markdown-preview text-[#F1F5F9]">
              <MarkdownContent content={renderedPrompt} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Simple markdown renderer without external dependency
function MarkdownContent({ content }) {
  // Parse and render markdown manually
  const renderMarkdown = (text) => {
    // Split into lines for processing
    const lines = text.split('\n');
    const elements = [];
    let currentList = [];
    let inCodeBlock = false;
    let codeContent = [];
    let codeLanguage = '';

    lines.forEach((line, index) => {
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${index}`} className="bg-[#030712] border border-[#1E293B] rounded p-3 my-2 overflow-x-auto">
              <code className="text-xs font-mono text-[#F1F5F9]">{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
          codeLanguage = line.slice(3);
        }
        return;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        return;
      }

      // Flush current list
      if (currentList.length > 0 && !line.match(/^[\-\*]\s/)) {
        elements.push(
          <ul key={`list-${index}`} className="list-disc list-inside my-2 space-y-1">
            {currentList.map((item, i) => (
              <li key={i} className="text-sm text-[#94A3B8]">{item}</li>
            ))}
          </ul>
        );
        currentList = [];
      }

      // Headers
      if (line.startsWith('### ')) {
        elements.push(<h3 key={index} className="text-base font-semibold text-[#F1F5F9] mt-4 mb-2">{line.slice(4)}</h3>);
      } else if (line.startsWith('## ')) {
        elements.push(<h2 key={index} className="text-lg font-semibold text-[#F1F5F9] mt-4 mb-2">{line.slice(3)}</h2>);
      } else if (line.startsWith('# ')) {
        elements.push(<h1 key={index} className="text-xl font-bold text-[#F1F5F9] mt-4 mb-2">{line.slice(2)}</h1>);
      }
      // Numbered lists
      else if (line.match(/^\d+\.\s/)) {
        const text = line.replace(/^\d+\.\s/, '');
        elements.push(<div key={index} className="text-sm text-[#94A3B8] ml-4">{line}</div>);
      }
      // Bullet lists
      else if (line.match(/^[\-\*]\s/)) {
        currentList.push(line.slice(2));
      }
      // Bold text and inline code
      else if (line.trim()) {
        let formatted = line
          .replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#F1F5F9]">$1</strong>')
          .replace(/`(.*?)`/g, '<code class="bg-[#1E293B] px-1 rounded text-[#0EA5E9] text-xs">$1</code>');
        elements.push(
          <p key={index} className="text-sm text-[#94A3B8] my-1" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
      // Empty lines
      else {
        elements.push(<div key={index} className="h-2" />);
      }
    });

    // Flush remaining list
    if (currentList.length > 0) {
      elements.push(
        <ul key="final-list" className="list-disc list-inside my-2 space-y-1">
          {currentList.map((item, i) => (
            <li key={i} className="text-sm text-[#94A3B8]">{item}</li>
          ))}
        </ul>
      );
    }

    return elements;
  };

  return <div>{renderMarkdown(content)}</div>;
}
